import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReaderBlock, ReaderMediaProgressItem } from '@bookclub/shared';
import { BlockRenderer } from '@/shared/components/blocks/BlockRenderer';
import type { RoundVideoPlaybackRate } from '@/shared/components/blocks/BlockRenderer';

type ReaderMediaState = ReaderMediaProgressItem['state'];

interface ReaderRoundVideoSequenceProps {
  sequenceId: string;
  blocks: ReaderBlock[];
  clubId: string;
  chapterId: string;
  defaultVideoPoster?: string | null;
  mediaProgressMap: Map<string, ReaderMediaProgressItem>;
  registerMediaElement?: (blockId: string, element: HTMLMediaElement | null) => void;
  onMediaPlay?: (blockId: string) => void;
  onMediaPause?: (blockId: string) => void;
  onVideoTimeUpdate?: (blockId: string, positionSeconds: number, durationSeconds: number) => void;
  onVideoEnded?: (blockId: string, positionSeconds: number, durationSeconds: number) => void;
  onOpen?: (payload: {
    clubId: string;
    chapterId: string;
    sequenceId: string;
    totalCount: number;
    viewedCount: number;
  }) => void;
  onTranscriptOpen?: (payload: {
    clubId: string;
    chapterId: string;
    sequenceId: string;
    blockId: string;
  }) => void;
}

function getBlockMediaState(progress: ReaderMediaProgressItem | undefined): ReaderMediaState {
  return progress?.state ?? 'not_started';
}

function getTranscriptText(block: ReaderBlock) {
  const transcriptText = block.payload['transcriptText'];
  return typeof transcriptText === 'string' ? transcriptText.trim() : '';
}

function getInitialActiveBlockId(blocks: ReaderBlock[], mediaProgressMap: Map<string, ReaderMediaProgressItem>) {
  return blocks.find((block) => mediaProgressMap.get(block.id)?.state !== 'completed')?.id ?? blocks[0]?.id ?? '';
}

export function ReaderRoundVideoSequence({
  sequenceId,
  blocks,
  clubId,
  chapterId,
  defaultVideoPoster,
  mediaProgressMap,
  registerMediaElement,
  onMediaPlay,
  onMediaPause,
  onVideoTimeUpdate,
  onVideoEnded,
  onOpen,
  onTranscriptOpen,
}: ReaderRoundVideoSequenceProps) {
  const [activeBlockId, setActiveBlockId] = useState(() => getInitialActiveBlockId(blocks, mediaProgressMap));
  const [openTranscriptBlockId, setOpenTranscriptBlockId] = useState<string | null>(null);
  const [playbackRate, setPlaybackRate] = useState<RoundVideoPlaybackRate>(1);
  const initialActiveBlockIdRef = useRef(activeBlockId);
  const initialScrolledBlockIdRef = useRef<string | null>(null);
  const playbackRateRef = useRef<RoundVideoPlaybackRate>(playbackRate);
  const itemElementsRef = useRef(new Map<string, HTMLDivElement>());
  const mediaElementsRef = useRef(new Map<string, HTMLMediaElement>());
  const totalCount = blocks.length;
  const viewedCount = useMemo(
    () => blocks.filter((block) => mediaProgressMap.get(block.id)?.state === 'completed').length,
    [blocks, mediaProgressMap],
  );

  useEffect(() => {
    if (!blocks.some((block) => block.id === activeBlockId)) {
      setActiveBlockId(getInitialActiveBlockId(blocks, mediaProgressMap));
    }
  }, [activeBlockId, blocks, mediaProgressMap]);

  useEffect(() => {
    playbackRateRef.current = playbackRate;
  }, [playbackRate]);

  useEffect(() => {
    if (activeBlockId !== initialActiveBlockIdRef.current || initialScrolledBlockIdRef.current === activeBlockId) {
      return;
    }

    const activeIndex = blocks.findIndex((block) => block.id === activeBlockId);
    if (activeIndex <= 0) {
      return;
    }

    const activeItemElement = itemElementsRef.current.get(activeBlockId);
    if (typeof activeItemElement?.scrollIntoView !== 'function') {
      return;
    }

    initialScrolledBlockIdRef.current = activeBlockId;
    activeItemElement.scrollIntoView({
      behavior: 'auto',
      block: 'nearest',
      inline: 'center',
    });
  }, [activeBlockId, blocks]);

  useEffect(() => {
    setOpenTranscriptBlockId(null);
  }, [activeBlockId]);

  useEffect(() => {
    onOpen?.({
      clubId,
      chapterId,
      sequenceId,
      totalCount,
      viewedCount,
    });
  }, [chapterId, clubId, onOpen, sequenceId, totalCount, viewedCount]);

  const handleRegisterMediaElement = useCallback(
    (blockId: string, element: HTMLMediaElement | null) => {
      if (element) {
        mediaElementsRef.current.set(blockId, element);
      } else {
        mediaElementsRef.current.delete(blockId);
      }

      registerMediaElement?.(blockId, element);
    },
    [registerMediaElement],
  );

  const handleVideoEnded = useCallback(
    (blockId: string, positionSeconds: number, durationSeconds: number) => {
      onVideoEnded?.(blockId, positionSeconds, durationSeconds);

      const currentIndex = blocks.findIndex((block) => block.id === blockId);
      if (currentIndex < 0 || currentIndex >= blocks.length - 1) {
        return;
      }

      const nextBlock = blocks[currentIndex + 1];
      setActiveBlockId(nextBlock.id);
      const nextItemElement = itemElementsRef.current.get(nextBlock.id);
      if (typeof nextItemElement?.scrollIntoView === 'function') {
        nextItemElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }

      window.setTimeout(() => {
        const nextMediaElement = mediaElementsRef.current.get(nextBlock.id);
        if (!nextMediaElement) {
          return;
        }

        nextMediaElement.playbackRate = playbackRateRef.current;
        void nextMediaElement.play()?.catch(() => undefined);
      }, 0);
    },
    [blocks, onVideoEnded],
  );

  return (
    <section
      data-testid={`round-video-sequence-${sequenceId}`}
      aria-label={`Круглые видео: ${viewedCount} из ${totalCount} просмотрено`}
      className="mx-1 rounded-[28px] border border-[rgba(139,111,72,0.18)] bg-[linear-gradient(135deg,rgba(255,248,235,0.92),rgba(242,229,205,0.72))] py-3 shadow-[0_14px_30px_rgba(62,48,23,0.08)] min-[430px]:mx-2"
    >
      <div className="flex items-center justify-between gap-3 px-3 min-[430px]:px-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[rgba(101,76,42,0.58)]">
            Видео-серия
          </p>
          <p className="mt-0.5 text-sm font-medium text-[rgba(58,49,39,0.82)]">
            {viewedCount} / {totalCount} просмотрено
          </p>
        </div>
        <span
          data-testid={`round-video-sequence-progress-${sequenceId}`}
          className="rounded-full bg-white/72 px-3 py-1 text-[12px] font-semibold tabular-nums text-[rgba(58,49,39,0.74)] shadow-[inset_0_0_0_1px_rgba(139,111,72,0.12)]"
        >
          {viewedCount}/{totalCount}
        </span>
      </div>

      <div className="mt-3 flex snap-x gap-3 overflow-x-auto px-3 pb-1 pt-1 min-[430px]:px-4">
        {blocks.map((block, index) => {
          const progress = mediaProgressMap.get(block.id);
          const isActive = block.id === activeBlockId;
          const transcriptText = getTranscriptText(block);
          const hasTranscript = transcriptText.length > 0;
          const isTranscriptOpen = openTranscriptBlockId === block.id;

          return (
            <div
              key={block.id}
              ref={(element) => {
                if (element) {
                  itemElementsRef.current.set(block.id, element);
                } else {
                  itemElementsRef.current.delete(block.id);
                }
              }}
              role="group"
              tabIndex={0}
              data-testid={`round-video-sequence-item-${block.id}`}
              data-active={isActive ? 'true' : 'false'}
              aria-label={`Видео ${index + 1} из ${totalCount}${isActive ? ', активное' : ''}`}
              onClick={() => setActiveBlockId(block.id)}
              onFocus={(event) => {
                if (event.target === event.currentTarget) {
                  setActiveBlockId(block.id);
                }
              }}
              className={[
                'shrink-0 snap-center rounded-[28px] border p-1.5 transition',
                isActive
                  ? 'border-[rgba(194,112,46,0.82)] bg-white shadow-[0_12px_24px_rgba(129,83,35,0.18)] ring-2 ring-[rgba(232,151,76,0.32)]'
                  : 'border-white/70 bg-white/44 shadow-[0_8px_18px_rgba(62,48,23,0.08)]',
              ].join(' ')}
            >
              <BlockRenderer
                block={block}
                defaultVideoPoster={defaultVideoPoster}
                mediaState={getBlockMediaState(progress)}
                registerMediaElement={handleRegisterMediaElement}
                onMediaPlay={(blockId) => {
                  setActiveBlockId(blockId);
                  onMediaPlay?.(blockId);
                }}
                onMediaPause={onMediaPause}
                videoPosition={progress?.state === 'in_progress' ? progress.positionSeconds : undefined}
                onVideoTimeUpdate={onVideoTimeUpdate}
                onVideoEnded={handleVideoEnded}
                roundVideoPlaybackRate={playbackRate}
                onRoundVideoPlaybackRateChange={setPlaybackRate}
              />
              {isActive && hasTranscript ? (
                <div className="px-1 pb-1 pt-2">
                  <button
                    type="button"
                    className="rounded-full bg-[rgba(58,49,39,0.08)] px-3 py-1.5 text-[12px] font-semibold text-[rgba(58,49,39,0.76)] transition hover:bg-[rgba(58,49,39,0.12)] focus:outline-none focus:ring-2 focus:ring-[rgba(194,112,46,0.36)]"
                    aria-expanded={isTranscriptOpen}
                    aria-controls={`round-video-transcript-${block.id}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setOpenTranscriptBlockId((current) => {
                        if (current === block.id) {
                          return null;
                        }

                        onTranscriptOpen?.({
                          clubId,
                          chapterId,
                          sequenceId,
                          blockId: block.id,
                        });
                        return block.id;
                      });
                    }}
                  >
                    Расшифровка
                  </button>
                  {isTranscriptOpen ? (
                    <div
                      id={`round-video-transcript-${block.id}`}
                      data-testid={`round-video-transcript-${block.id}`}
                      className="mt-2 max-w-[13.5rem] whitespace-pre-wrap rounded-2xl bg-[rgba(255,248,235,0.92)] px-3 py-2 text-[13px] leading-[1.45] text-[rgba(58,49,39,0.78)] shadow-[inset_0_0_0_1px_rgba(139,111,72,0.1)]"
                    >
                      {transcriptText}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}
